import { createFileRoute, useMatches } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plug,
  User,
  Mail,
  CircleDot,
  Settings,
  Loader2,
  Link2,
  Link2Off,
  CircleX,
  HelpCircle,
  Trash2,
  Pencil,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InlineSvg } from "@/components/ui/inline-svg";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import emailSvg from "@/assets/icons/mail-s.svg?raw";
import linkedinSvg from "@/assets/icons/linkedin-s.svg?raw";
import phoneSvg from "@/assets/icons/phone.svg?raw";
import whatsappSvg from "@/assets/icons/whatsapp.svg?raw";
import facebookSvg from "@/assets/icons/facebook.svg?raw";
import slackSvg from "@/assets/icons/slack.svg?raw";
import larkSvg from "@/assets/icons/lark.svg?raw";
import wecomSvg from "@/assets/icons/wecom.svg?raw";
import { CustomPagination } from "@/components/ui/custom-pagination";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { listIntegrationAccountsServer } from "@/server/user-integrations";
import { createUnipileConnectionServer } from "@/server/user-integrations";
import { saveYCloudWhatsappBusinessTokenServer } from "@/server/user-integrations";
import {
  createEmailAccountServer,
  listEmailAccountsServer,
  suspendEmailAccountServer,
  activateEmailAccountServer,
} from "@/server/email-accounts";
import {
  listNotificationChannelsServer,
  createNotificationChannelServer,
  deleteNotificationChannelServer,
  setNotificationPreferenceServer,
  getUserNotificationPreferencesServer,
  updateNotificationChannelServer,
  batchSetNotificationPreferencesServer,
} from "@/server/notifications";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { WORKUS_EMAIL_DOMAIN, WORKUS_EMAIL_DOMAIN_SUFFIX } from "@/lib/constants";
import type {
  UserIntegrationAccountResponse,
  NotificationChannelType,
  NotificationEventType,
} from "@/lib/api/generated";
import type { ChannelResponse } from "@/lib/api/generated";
import { NotificationChannelDialog } from "@/components/settings/notification-channel-dialog";
import { EditNotificationChannelDialog } from "@/components/settings/edit-notification-channel-dialog";
import { UpgradePlanModal } from "@/components/billing/UpgradePlanModal";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";
import type { WorkspaceLayoutLoaderData } from "@/routes/workspace/$workspaceSlug/route";

const pageSize = 4;
const maxHostedMailBatchSize = 10;
export const Route = createFileRoute("/workspace/$workspaceSlug/settings/workspace/integrations")({
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "settings.workspace.integrations",
      parentMatchPromise,
    );

    const [result, emailAccounts] = await Promise.all([
      listIntegrationAccountsServer({
        data: {
          workspaceId,
          params: { page: 1, page_size: pageSize },
        },
      }),
      listEmailAccountsServer({ data: workspaceId }),
    ]);

    const currentPage = result.page;
    const totalPages = Math.max(1, Math.ceil(result.total / result.page_size));

    return {
      workspaceId,
      accounts: result.accounts as UserIntegrationAccountResponse[],
      total: result.total,
      page: currentPage,
      pageSize: pageSize,
      totalPages,
      notificationChannels: (result.notification_channels || []) as ChannelResponse[],
      emailAccountsTotal: emailAccounts.total ?? 0,
    };
  },
  component: IntegrationsPage,
});

function IntegrationsPage() {
  const { t } = useTranslation(["settings", "billing"]);
  const initial = Route.useLoaderData() as {
    workspaceId: string;
    accounts: UserIntegrationAccountResponse[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    notificationChannels: ChannelResponse[];
    emailAccountsTotal: number;
  };
  const [accounts, setAccounts] = useState<UserIntegrationAccountResponse[]>(initial.accounts);
  const [page, setPage] = useState<number>(initial.page);
  const [total, setTotal] = useState<number>(initial.total);
  const [totalPages, setTotalPages] = useState<number>(initial.totalPages);
  const [notificationChannels, setNotificationChannels] = useState<ChannelResponse[]>(
    initial.notificationChannels,
  );
  const [emailAccountsTotal, setEmailAccountsTotal] = useState<number>(initial.emailAccountsTotal);
  const workspaceId = initial.workspaceId;
  const [loading, setLoading] = useState(false);
  const [hostedMailOpen, setHostedMailOpen] = useState(false);
  const [hostedMailUsernames, setHostedMailUsernames] = useState<string[]>([""]);
  const [whatsappBusinessOpen, setWhatsappBusinessOpen] = useState(false);
  const [whatsappBusinessApiKey, setWhatsappBusinessApiKey] = useState("");
  const [whatsappBusinessPhoneNumber, setWhatsappBusinessPhoneNumber] = useState("");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeNoticeTitle, setUpgradeNoticeTitle] = useState<string | undefined>(undefined);
  const [upgradeNoticeDescription, setUpgradeNoticeDescription] = useState<string | undefined>(
    undefined,
  );

  // Notification channel dialog state
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<NotificationChannelType | null>(
    null,
  );
  const [editingChannel, setEditingChannel] = useState<ChannelResponse | null>(null);
  const [editingChannelEvents, setEditingChannelEvents] = useState<NotificationEventType[]>([]);
  const matches = useMatches();
  const workspaceMatch = matches.find((match) => match.routeId === "/workspace/$workspaceSlug");
  const workspaceData = workspaceMatch?.loaderData as WorkspaceLayoutLoaderData | undefined;
  const currentPlan = workspaceData?.billingSummary?.subscription?.plan_type?.toLowerCase() ?? "";
  const workspaceSlug = workspaceData?.workspaceSlug ?? "";
  const hostedEmailLimit = useMemo(() => {
    switch (currentPlan) {
      case "starter":
        return 3;
      case "professional":
        return 10;
      case "enterprise":
        return -1;
      default:
        return -1;
    }
  }, [currentPlan]);
  const isStarterPlan = currentPlan === "starter";
  const isHostedMailLimitReached = hostedEmailLimit >= 0 && emailAccountsTotal >= hostedEmailLimit;

  const providerIconForType = (t: string) => {
    switch (t) {
      case "gmail":
      case "workus_email":
        return <InlineSvg content={emailSvg} size={16} />;
      case "linkedin":
        return <InlineSvg content={linkedinSvg} size={16} />;
      case "whatsapp":
        return <InlineSvg content={phoneSvg} size={16} />;
      case "whatsapp_business":
        return <InlineSvg content={whatsappSvg} size={16} />;
      case "facebook":
        return <InlineSvg content={facebookSvg} size={16} />;
      default:
        return <Plug className="size-4 text-muted-foreground" />;
    }
  };

  const openUpgradeModal = (title: string, description: string) => {
    setUpgradeNoticeTitle(title);
    setUpgradeNoticeDescription(description);
    setUpgradeModalOpen(true);
  };

  const openHostedMailLimitUpgradeModal = () => {
    openUpgradeModal(
      t("settings:workspace.integrationsPage.paywall.hostedMailLimitTitle"),
      t("settings:workspace.integrationsPage.paywall.hostedMailLimitDescription", {
        limit: hostedEmailLimit,
        plan: t(`billing:plans.${currentPlan || "starter"}`),
      }),
    );
  };

  const connectionStatusForType = (t: string) => {
    switch (t) {
      case "connected":
        return <Link2 className="size-4 text-[#00B205]" />;
      case "disconnected":
        return <Link2Off className="size-4 text-[#FF9113]" />;
      case "failed":
        return <CircleX className="size-4 text-[#FF135D]" />;
      default:
        return <CircleDot className="size-4 text-[#007BFF]" />;
    }
  };

  const displayAccountName = (acc: UserIntegrationAccountResponse) => {
    if (String(acc.integration_type) === "workus_email") {
      const ident = acc.account_identifier ?? "";
      const prefix = ident.includes("@") ? ident.split("@")[0] : ident;
      return prefix || acc.account_name;
    }
    return acc.account_name;
  };

  const displayAccountIdentifier = (acc: UserIntegrationAccountResponse) => {
    if (String(acc.integration_type) === "whatsapp_business") {
      const ident = acc.account_identifier ?? "";
      const prefix = ident.slice(0, 3);
      return `${prefix}**********`;
    }
    return acc.account_identifier;
  };

  // Notification channel handlers
  const handleDeleteChannel = async (channelId: string) => {
    try {
      setLoading(true);
      await deleteNotificationChannelServer({
        data: { channelId },
      });
      // Refresh channels
      const channels = await listNotificationChannelsServer({
        data: { workspaceId },
      });
      setNotificationChannels(channels);
      toast.success(t("workspace.integrationsPage.channelDialog.deleteSuccess"));
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        t("workspace.integrationsPage.channelDialog.deleteFailed", { error: "" }),
      );
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddChannel = (type: NotificationChannelType) => {
    setSelectedChannelType(type);
    setEditingChannel(null);
    setEditingChannelEvents([]);
    setAddChannelOpen(true);
  };

  const handleOpenEditChannel = async (channel: ChannelResponse) => {
    setSelectedChannelType(channel.channel_type as NotificationChannelType);
    setEditingChannel(channel);

    // 加载用户的通知偏好
    let enabledEvents: NotificationEventType[] = [];
    try {
      const prefs = await getUserNotificationPreferencesServer({
        data: { workspaceId },
      });

      // 找出该渠道已启用的事件
      for (const [event, channelIds] of Object.entries(prefs)) {
        if (channelIds.includes(channel.id)) {
          enabledEvents.push(event as NotificationEventType);
        }
      }

      logInfo("integrations.channel.edit_opened", {
        channel_id: channel.id,
        channel_name: channel.name,
        enabled_events: enabledEvents,
        total_preferences: Object.keys(prefs).length,
      });
    } catch (error) {
      // 如果获取偏好失败（可能是后端错误或没有数据），默认为空列表
      // 用户仍然可以编辑并重新设置偏好
      logError("integrations.channel.load_events_failed", {
        error: error instanceof Error ? error.message : "unknown-error",
        channel_id: channel.id,
      });
      enabledEvents = [];
    }

    // 先设置事件，然后在下一个微任务中打开弹窗，确保状态已更新
    setEditingChannelEvents(enabledEvents);
    // 使用 setTimeout 0 确保状态更新后再打开弹窗
    setTimeout(() => {
      setAddChannelOpen(true);
    }, 0);
  };

  const handleCreateChannel = async (data: {
    name: string;
    webhookUrl: string;
    enabledEvents: NotificationEventType[];
  }) => {
    if (!selectedChannelType) return;

    try {
      setLoading(true);

      // 创建渠道
      await createNotificationChannelServer({
        data: {
          workspaceId,
          payload: {
            channel_type: selectedChannelType,
            name: data.name,
            webhook_url: data.webhookUrl,
          },
        },
      });

      // Refresh channels to get the newly created channel ID
      const channels = await listNotificationChannelsServer({
        data: { workspaceId },
      });
      setNotificationChannels(channels);

      // Find the newly created channel
      const newChannel = channels.find(
        (ch) => ch.channel_type === selectedChannelType && ch.name === data.name,
      );

      // 为每个启用的事件类型设置通知偏好
      if (newChannel && data.enabledEvents.length > 0) {
        // 获取当前用户的所有偏好，避免覆盖现有订阅
        const currentPrefs = await getUserNotificationPreferencesServer({
          data: { workspaceId },
        });

        for (const eventType of data.enabledEvents) {
          // 获取该事件类型当前订阅的所有渠道
          const existingChannelIds = currentPrefs[eventType] || [];
          // 添加新渠道到现有渠道列表
          const updatedChannelIds = [...new Set([...existingChannelIds, newChannel.id])];

          await setNotificationPreferenceServer({
            data: {
              workspaceId,
              payload: {
                event_type: eventType,
                channel_ids: updatedChannelIds,
                is_enabled: true,
              },
            },
          });
        }
      }

      setAddChannelOpen(false);
      toast.success(t("workspace.integrationsPage.channelDialog.createSuccess"));
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to create channel");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChannel = async (data: {
    name: string;
    webhookUrl?: string;
    enabledEvents: NotificationEventType[];
  }) => {
    if (!editingChannel) return;

    try {
      setLoading(true);

      // 更新渠道（包含名称和 webhook URL）
      await updateNotificationChannelServer({
        data: {
          channelId: editingChannel.id,
          payload: {
            name: data.name,
            webhook_url: data.webhookUrl,
          },
        },
      });

      // 获取渠道当前的所有通知偏好
      const currentPrefs = await getUserNotificationPreferencesServer({
        data: { workspaceId },
      });

      // 获取所有支持的事件类型
      const allEventTypes: NotificationEventType[] = [
        "customer_reply_received",
        "daily_report",
        "weekly_report",
      ];

      // 计算每个事件需要更新后的 channel_ids
      const preferencesToUpdate: {
        event_type: NotificationEventType;
        channel_ids: string[];
        is_enabled: boolean;
      }[] = [];

      for (const eventType of allEventTypes) {
        const existingChannelIds = currentPrefs[eventType] || [];
        const isEventSelected = data.enabledEvents.includes(eventType);
        const isChannelInEvent = existingChannelIds.includes(editingChannel.id);

        if (isEventSelected && !isChannelInEvent) {
          // 添加渠道到该事件：合并现有渠道ID和新渠道ID
          const updatedChannelIds = [...new Set([...existingChannelIds, editingChannel.id])];
          preferencesToUpdate.push({
            event_type: eventType,
            channel_ids: updatedChannelIds,
            is_enabled: true,
          });
        } else if (!isEventSelected && isChannelInEvent) {
          // 从该事件移除渠道：过滤掉当前渠道ID
          const updatedChannelIds = existingChannelIds.filter((id) => id !== editingChannel.id);
          if (updatedChannelIds.length > 0) {
            // 还有其他渠道，更新列表
            preferencesToUpdate.push({
              event_type: eventType,
              channel_ids: updatedChannelIds,
              is_enabled: true,
            });
          } else {
            // 没有其他渠道，禁用该偏好
            preferencesToUpdate.push({
              event_type: eventType,
              channel_ids: updatedChannelIds,
              is_enabled: false,
            });
          }
        }
        // 如果事件状态没变，跳过
      }

      // 批量更新所有偏好（一次请求）
      if (preferencesToUpdate.length > 0) {
        await batchSetNotificationPreferencesServer({
          data: {
            workspaceId,
            preferences: preferencesToUpdate,
          },
        });

        logInfo("integrations.channel.preferences_updated", {
          channel_id: editingChannel.id,
          channel_name: editingChannel.name,
          updated_count: preferencesToUpdate.length,
          enabled_events: data.enabledEvents,
        });
      }

      // 刷新渠道列表
      const channels = await listNotificationChannelsServer({
        data: { workspaceId },
      });
      setNotificationChannels(channels);

      setAddChannelOpen(false);
      setEditingChannel(null);
      toast.success(t("workspace.integrationsPage.channelDialog.updateSuccess"));
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to update channel");
      toast.error(message);
      logError("integrations.channel.update_failed", {
        error: error instanceof Error ? error.message : "unknown-error",
        channel_id: editingChannel.id,
      });
    } finally {
      setLoading(false);
    }
  };

  const getChannelTypeLabel = (type: string) => {
    switch (type.toLowerCase()) {
      case "slack":
        return "Slack";
      case "lark":
        return "Lark";
      case "wecom":
        return "WeCom";
      case "email":
        return "Email";
      default:
        return type;
    }
  };

  const handlePageChange = async (p: number) => {
    setLoading(true);
    try {
      const result = await listIntegrationAccountsServer({
        data: { workspaceId, params: { page: p, page_size: pageSize } },
      });
      const emailAccounts = await listEmailAccountsServer({ data: workspaceId });
      const currentPage = result.page;
      const nextTotalPages = Math.max(1, Math.ceil(result.total / result.page_size));
      setAccounts(result.accounts as UserIntegrationAccountResponse[]);
      if (result.notification_channels) {
        setNotificationChannels(result.notification_channels as ChannelResponse[]);
      }
      setTotal(result.total);
      setPage(currentPage);
      setTotalPages(nextTotalPages);
      setEmailAccountsTotal(emailAccounts.total ?? 0);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <div className="text-[16px] font-semibold text-gray-900 pl-6 pt-6">
        {t("workspace.integrationsPage.title")}
      </div>
      <div className="text-subtle text-[12px] pl-6">{t("workspace.integrationsPage.subtitle")}</div>
      <div
        className="ml-6 mr-4 rounded-xl border border-[#DFDFDF] bg-white mt-4 relative overflow-hidden"
        aria-busy={loading}
      >
        {loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <Table>
          <TableHeader className="bg-[#F8F8F8] [&_th]:border-r [&_th]:border-[#DFDFDF] [&_th:last-child]:border-r-0">
            <TableRow>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  <Plug className="size-4 text-muted-foreground" />
                  {t("workspace.integrationsPage.table.provider")}
                </span>
              </TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  <User className="size-4 text-muted-foreground" />
                  {t("workspace.integrationsPage.table.accountName")}
                </span>
              </TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  <Mail className="size-4 text-muted-foreground" />
                  {t("workspace.integrationsPage.table.account")}
                </span>
              </TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  <CircleDot className="size-4 text-muted-foreground" />
                  {t("workspace.integrationsPage.table.status")}
                </span>
              </TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  <Settings className="size-4 text-muted-foreground" />
                  {t("workspace.integrationsPage.table.actions")}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="[&_td]:border-r [&_td]:border-[#DFDFDF] [&_td:last-child]:border-r-0">
            {notificationChannels.map((channel) => (
              <TableRow key={channel.id}>
                <td className="p-2 align-middle whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    {(() => {
                      switch (channel.channel_type.toLowerCase()) {
                        case "slack":
                          return <InlineSvg content={slackSvg} size={16} />;
                        case "lark":
                          return <InlineSvg content={larkSvg} size={16} />;
                        case "wecom":
                          return <InlineSvg content={wecomSvg} size={16} />;
                        case "email":
                          return <InlineSvg content={emailSvg} size={16} />;
                        default:
                          return <Plug className="size-4 text-muted-foreground" />;
                      }
                    })()}
                    {getChannelTypeLabel(channel.channel_type)}
                  </span>
                </td>
                <td className="p-2 align-middle whitespace-nowrap">{channel.name}</td>
                <td className="p-2 align-middle">
                  <div
                    className="max-w-[200px] truncate text-[#007BFF]"
                    title={
                      channel.channel_type.toLowerCase() === "email"
                        ? channel.display_name
                        : channel.webhook_url || channel.display_name
                    }
                  >
                    {channel.channel_type.toLowerCase() === "email"
                      ? channel.display_name
                      : channel.webhook_url || channel.display_name}
                  </div>
                </td>
                <td className="p-2 align-middle whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    {channel.is_active ? (
                      <Link2 className="size-4 text-[#00B205]" />
                    ) : (
                      <Link2Off className="size-4 text-[#FF9113]" />
                    )}
                    {channel.is_active ? "Connected" : "Inactive"}
                  </span>
                </td>
                <td className="p-2 align-middle whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleOpenEditChannel(channel)}
                          disabled={loading}
                          aria-label="Edit"
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="center">
                        Edit
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => channel.id && handleDeleteChannel(channel.id)}
                          disabled={loading}
                          aria-label="Delete"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="center">
                        Delete
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </td>
              </TableRow>
            ))}

            {accounts.map((acc) => (
              <TableRow key={acc.id}>
                <td className="p-2 align-middle whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    {providerIconForType(String(acc.integration_type))}
                    {acc.integration_type}
                  </span>
                </td>
                <td className="p-2 align-middle whitespace-nowrap">{displayAccountName(acc)}</td>
                <td className="p-2 align-middle">
                  <div
                    className="max-w-[200px] truncate text-[#007BFF]"
                    title={displayAccountIdentifier(acc) || ""}
                  >
                    {displayAccountIdentifier(acc)}
                  </div>
                </td>
                <td className="p-2 align-middle whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    {connectionStatusForType(String(acc.connection_status))}
                    {acc.connection_status}
                  </span>
                </td>
                <td className="p-2 align-middle whitespace-nowrap">
                  {String(acc.connection_status) === "disconnected" ||
                  String(acc.connection_status) === "disconnect" ? (
                    <Button
                      variant="outline"
                      className="h-8 px-2 inline-flex items-center gap-2"
                      disabled={loading}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          if (String(acc.integration_type) === "workus_email") {
                            await activateEmailAccountServer({
                              data: { workspaceId, accountId: acc.id },
                            });
                            toast.success(
                              t("workspace.integrationsPage.messages.reconnectSuccess"),
                            );
                          } else {
                            toast.info(t("workspace.integrationsPage.messages.onlyEmailReconnect"));
                          }
                        } catch (e) {
                          const msg = e instanceof Error ? e.message : String(e);
                          toast.error(
                            t("workspace.integrationsPage.messages.reconnectFailed", {
                              error: msg,
                            }),
                          );
                        } finally {
                          setLoading(false);
                          await handlePageChange(page);
                        }
                      }}
                    >
                      <Link2 className="size-4 text-[#00B205]" />
                      {t("workspace.integrationsPage.actions.reconnect")}
                    </Button>
                  ) : String(acc.connection_status) === "connected" ? (
                    <Button
                      variant="outline"
                      className="h-8 px-2 inline-flex items-center gap-2"
                      disabled={loading}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          if (String(acc.integration_type) === "workus_email") {
                            await suspendEmailAccountServer({
                              data: {
                                workspaceId,
                                accountId: acc.id,
                                reason: "User requested disconnect",
                              },
                            });
                            toast.success(
                              t("workspace.integrationsPage.messages.disconnectSuccess"),
                            );
                          } else {
                            toast.info(
                              t("workspace.integrationsPage.messages.onlyEmailDisconnect"),
                            );
                          }
                        } catch (e) {
                          const msg = e instanceof Error ? e.message : String(e);
                          toast.error(
                            t("workspace.integrationsPage.messages.disconnectFailed", {
                              error: msg,
                            }),
                          );
                        } finally {
                          setLoading(false);
                          await handlePageChange(page);
                        }
                      }}
                    >
                      <Link2Off className="size-4 text-[#FF135D]" />
                      {t("workspace.integrationsPage.actions.disconnect")}
                    </Button>
                  ) : null}
                </td>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="p-4">
        <CustomPagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => {
            void size;
          }}
          showPageSize={false}
        />
      </div>

      <div className="text-[16px] font-semibold text-gray-900 pl-6 mt-2 mb-4">
        {t("workspace.integrationsPage.channel")}
      </div>
      <div className="px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-4">
        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="text-[14px]">
              {t("workspace.integrationsPage.providers.hostedMail.title")}
            </CardTitle>
            <CardDescription className="text-[12px] min-h-[50px]">
              {t("workspace.integrationsPage.providers.hostedMail.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            {isHostedMailLimitReached ? (
              <Button
                variant="outline"
                className="w-full justify-center gap-2 text-[12px] font-normal"
                onClick={openHostedMailLimitUpgradeModal}
              >
                <InlineSvg content={emailSvg} size={16} />
                {t("workspace.integrationsPage.paywall.upgradeToConnect")}
              </Button>
            ) : (
              <Dialog open={hostedMailOpen} onOpenChange={setHostedMailOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-center gap-2 text-[12px] font-normal"
                    onClick={() => setHostedMailOpen(true)}
                  >
                    <InlineSvg content={emailSvg} size={16} />
                    {t("workspace.integrationsPage.providers.hostedMail.connect")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {t("workspace.integrationsPage.providers.hostedMail.dialogTitle")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {hostedMailUsernames.map((name, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 rounded-[12px] border border-[#DFDFDF] bg-white px-3 py-2"
                      >
                        <Input
                          placeholder="Please enter"
                          value={name}
                          onChange={(e) => {
                            const v = e.target.value;
                            setHostedMailUsernames((prev) => {
                              const next = [...prev];
                              next[idx] = v;
                              return next;
                            });
                          }}
                          className="h-9 flex-1 border-none shadow-none focus-visible:ring-0 focus-visible:border-none"
                        />
                        <span className="text-sm font-medium text-[#8b8b8b]">
                          {WORKUS_EMAIL_DOMAIN}
                        </span>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="flex h-10 w-full items-center justify-center gap-2 rounded-[12px] border border-dashed border-[#DFDFDF] bg-white text-sm font-medium text-[#171717] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        const remaining =
                          hostedEmailLimit < 0
                            ? maxHostedMailBatchSize
                            : Math.max(hostedEmailLimit - emailAccountsTotal, 0);
                        if (remaining <= hostedMailUsernames.length) {
                          setHostedMailOpen(false);
                          openHostedMailLimitUpgradeModal();
                          return;
                        }
                        setHostedMailUsernames((prev) =>
                          prev.length >= maxHostedMailBatchSize ? prev : [...prev, ""],
                        );
                      }}
                      disabled={
                        hostedMailUsernames.length >= maxHostedMailBatchSize ||
                        (hostedEmailLimit >= 0 &&
                          emailAccountsTotal + hostedMailUsernames.length >= hostedEmailLimit)
                      }
                    >
                      <Plus className="size-4" />{" "}
                      {t("workspace.integrationsPage.providers.hostedMail.addNew")}
                    </button>
                  </div>
                  <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => setHostedMailOpen(false)}>
                      {t("workspace.integrationsPage.actions.cancel")}
                    </Button>
                    <Button
                      className="cursor-pointer"
                      disabled={!hostedMailUsernames.some((u) => u.trim().length > 0) || loading}
                      onClick={async () => {
                        const usernames = hostedMailUsernames
                          .map((u) => u.trim())
                          .filter((u) => u.length > 0)
                          .slice(0, maxHostedMailBatchSize);

                        if (usernames.length === 0) {
                          setHostedMailOpen(false);
                          return;
                        }

                        if (
                          hostedEmailLimit >= 0 &&
                          emailAccountsTotal + usernames.length > hostedEmailLimit
                        ) {
                          setHostedMailOpen(false);
                          openHostedMailLimitUpgradeModal();
                          return;
                        }

                        try {
                          setLoading(true);
                          const results = await Promise.allSettled(
                            usernames.map((username) =>
                              createEmailAccountServer({
                                data: {
                                  workspaceId,
                                  username,
                                  domainSuffix: WORKUS_EMAIL_DOMAIN_SUFFIX,
                                },
                              }),
                            ),
                          );
                          const successCount = results.filter(
                            (r) => r.status === "fulfilled",
                          ).length;
                          const failed = results
                            .map((r, i) => (r.status === "rejected" ? usernames[i] : null))
                            .filter(Boolean) as string[];
                          if (successCount > 0) {
                            await handlePageChange(page);
                            toast.success(
                              t("workspace.integrationsPage.providers.hostedMail.createSuccess", {
                                count: successCount,
                              }),
                            );
                          }
                          if (failed.length > 0) {
                            toast.error(
                              t("workspace.integrationsPage.providers.hostedMail.createFailed", {
                                names: failed.join(", "),
                              }),
                            );
                          }
                        } finally {
                          setLoading(false);
                          setHostedMailOpen(false);
                        }
                      }}
                    >
                      {t("workspace.integrationsPage.actions.create")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardFooter>
        </Card>

        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-[14px]">
              <span>{t("workspace.integrationsPage.providers.linkedin.title")}</span>
              {isStarterPlan ? (
                <Badge
                  variant="outline"
                  className="shrink-0 border-blue-200 bg-blue-50 text-[11px] font-medium text-blue-700 hover:bg-blue-50"
                >
                  {t("workspace.integrationsPage.paywall.professionalBadge")}
                </Badge>
              ) : null}
            </CardTitle>
            <CardDescription className="min-h-[50px] text-[12px]">
              {t("workspace.integrationsPage.providers.linkedin.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              variant="outline"
              className="w-full justify-center gap-2 text-[12px] font-normal"
              onClick={async () => {
                if (isStarterPlan) {
                  openUpgradeModal(
                    t("settings:workspace.integrationsPage.paywall.proFeatureTitle"),
                    t("settings:workspace.integrationsPage.paywall.linkedinProDescription"),
                  );
                  return;
                }
                setLoading(true);
                try {
                  const res = await createUnipileConnectionServer({
                    data: { workspaceId, integrationType: "linkedin" },
                  });
                  toast.success(t("workspace.integrationsPage.providers.linkedin.redirecting"));
                  window.location.href = res.url;
                } catch (e) {
                  const msg = e instanceof Error ? e.message : String(e);
                  toast.error(`Failed to start LinkedIn connect: ${msg}`);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <InlineSvg content={linkedinSvg} size={16} />
              {isStarterPlan
                ? t("workspace.integrationsPage.paywall.upgradeToConnect")
                : t("workspace.integrationsPage.providers.linkedin.connect")}
            </Button>
          </CardFooter>
        </Card>

        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-[14px]">
              <span>{t("workspace.integrationsPage.providers.whatsapp.title")}</span>
              {isStarterPlan ? (
                <Badge
                  variant="outline"
                  className="shrink-0 border-blue-200 bg-blue-50 text-[11px] font-medium text-blue-700 hover:bg-blue-50"
                >
                  {t("workspace.integrationsPage.paywall.professionalBadge")}
                </Badge>
              ) : null}
            </CardTitle>
            <CardDescription className="min-h-[50px] text-[12px]">
              {t("workspace.integrationsPage.providers.whatsapp.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              variant="outline"
              className="w-full justify-center gap-2 text-[12px] font-normal"
              onClick={async () => {
                if (isStarterPlan) {
                  openUpgradeModal(
                    t("settings:workspace.integrationsPage.paywall.proFeatureTitle"),
                    t("settings:workspace.integrationsPage.paywall.whatsappProDescription"),
                  );
                  return;
                }
                setLoading(true);
                try {
                  const res = await createUnipileConnectionServer({
                    data: { workspaceId, integrationType: "whatsapp" },
                  });
                  toast.success(t("workspace.integrationsPage.providers.whatsapp.redirecting"));
                  window.location.href = res.url;
                } catch (e) {
                  const msg = e instanceof Error ? e.message : String(e);
                  toast.error(`Failed to start WhatsApp connect: ${msg}`);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <InlineSvg content={phoneSvg} size={16} />
              {isStarterPlan
                ? t("workspace.integrationsPage.paywall.upgradeToConnect")
                : t("workspace.integrationsPage.providers.whatsapp.connect")}
            </Button>
          </CardFooter>
        </Card>

        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-[14px]">
              <span>{t("workspace.integrationsPage.providers.whatsappBusiness.title")}</span>
              {isStarterPlan ? (
                <Badge
                  variant="outline"
                  className="shrink-0 border-blue-200 bg-blue-50 text-[11px] font-medium text-blue-700 hover:bg-blue-50"
                >
                  {t("workspace.integrationsPage.paywall.professionalBadge")}
                </Badge>
              ) : null}
            </CardTitle>
            <CardDescription className="text-[12px] min-h-[50px]">
              {t("workspace.integrationsPage.providers.whatsappBusiness.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            {isStarterPlan ? (
              <Button
                variant="outline"
                className="w-full justify-center gap-2 text-[12px] font-normal"
                onClick={() =>
                  openUpgradeModal(
                    t("settings:workspace.integrationsPage.paywall.proFeatureTitle"),
                    t("settings:workspace.integrationsPage.paywall.whatsappBusinessProDescription"),
                  )
                }
              >
                <InlineSvg content={whatsappSvg} size={16} />
                {t("workspace.integrationsPage.paywall.upgradeToConnect")}
              </Button>
            ) : (
              <Dialog open={whatsappBusinessOpen} onOpenChange={setWhatsappBusinessOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-center gap-2 text-[12px] font-normal"
                    onClick={() => setWhatsappBusinessOpen(true)}
                  >
                    <InlineSvg content={whatsappSvg} size={16} />
                    {t("workspace.integrationsPage.providers.whatsappBusiness.connect")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {t("workspace.integrationsPage.providers.whatsappBusiness.dialogTitle")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="text-[14px]">
                      {t("workspace.integrationsPage.providers.whatsappBusiness.apiKeyLabel")}
                    </div>
                    <div className="flex items-center gap-2 rounded-[12px] border border-[#DFDFDF] bg-white px-1">
                      <Input
                        placeholder="Please enter"
                        value={whatsappBusinessApiKey}
                        onChange={(e) => {
                          const v = e.target.value;
                          const sanitized = v.replace(/[\u3400-\u4dbf\u4e00-\u9fff]/g, "");
                          setWhatsappBusinessApiKey(sanitized);
                        }}
                        className="h-10 flex-1 border-none shadow-none focus-visible:ring-0 focus-visible:border-none"
                      />
                    </div>
                    <div className="text-[14px]">
                      {t("workspace.integrationsPage.providers.whatsappBusiness.phoneNumberLabel")}
                    </div>
                    <div className="flex items-center gap-2 rounded-[12px] border border-[#DFDFDF] bg-white px-1">
                      <Input
                        placeholder="please enter"
                        value={whatsappBusinessPhoneNumber}
                        onChange={(e) => {
                          const v = e.target.value;
                          const sanitized = v.replace(/[^\d+]/g, "");
                          setWhatsappBusinessPhoneNumber(sanitized);
                        }}
                        className="h-10 flex-1 border-none shadow-none focus-visible:ring-0 focus-visible:border-none"
                      />
                    </div>
                    <a
                      href="https://www.ycloud.com/console/#/app/developers/apikey"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm underline"
                    >
                      <HelpCircle className="size-4" />{" "}
                      {t("workspace.integrationsPage.providers.whatsappBusiness.helpLink")}
                    </a>
                  </div>
                  <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => setWhatsappBusinessOpen(false)}>
                      {t("workspace.integrationsPage.actions.cancel")}
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          await saveYCloudWhatsappBusinessTokenServer({
                            data: {
                              workspaceId,
                              token: whatsappBusinessApiKey.trim(),
                              accountIdentifier: whatsappBusinessPhoneNumber.trim(),
                            },
                          });
                          setWhatsappBusinessOpen(false);
                          setWhatsappBusinessApiKey("");
                          toast.success(
                            t("workspace.integrationsPage.providers.whatsappBusiness.saveSuccess"),
                          );
                        } catch (e) {
                          const msg = e instanceof Error ? e.message : String(e);
                          toast.error(`Failed to save API key: ${msg}`);
                        }
                      }}
                      disabled={!whatsappBusinessApiKey.trim()}
                    >
                      {t("workspace.integrationsPage.actions.create")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardFooter>
        </Card>
      </div>

      <div className="text-[16px] font-semibold text-gray-900 pl-6 mt-2 mb-4">
        {t("workspace.integrationsPage.notification")}
      </div>
      <div className="px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-4">
        {/* Slack Channel Card */}
        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="text-[14px]">
              {t("workspace.integrationsPage.notificationChannels.slack.title")}
            </CardTitle>
            <CardDescription className="text-[12px] min-h-[50px]">
              {t("workspace.integrationsPage.notificationChannels.slack.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-2">
            <Button
              variant="outline"
              className="w-full justify-center gap-2 text-[12px] font-normal"
              onClick={() => handleOpenAddChannel("slack")}
            >
              <InlineSvg content={slackSvg} size={16} />
              {t("workspace.integrationsPage.notificationChannels.slack.connect")}
            </Button>
            {notificationChannels.filter((c) => c.channel_type.toLowerCase() === "slack").length >
              0 && (
              <div className="w-full text-xs text-gray-600 text-center py-2">
                {
                  notificationChannels.filter((c) => c.channel_type.toLowerCase() === "slack")
                    .length
                }{" "}
                channel(s) configured
              </div>
            )}
          </CardFooter>
        </Card>

        {/* Lark Channel Card */}
        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="text-[14px]">
              {t("workspace.integrationsPage.notificationChannels.lark.title")}
            </CardTitle>
            <CardDescription className="text-[12px] min-h-[50px]">
              {t("workspace.integrationsPage.notificationChannels.lark.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-2">
            <Button
              variant="outline"
              className="w-full justify-center gap-2 text-[12px] font-normal"
              onClick={() => handleOpenAddChannel("lark")}
            >
              <InlineSvg content={larkSvg} size={16} />
              {t("workspace.integrationsPage.notificationChannels.lark.connect")}
            </Button>
            {notificationChannels.filter((c) => c.channel_type.toLowerCase() === "lark").length >
              0 && (
              <div className="w-full text-xs text-gray-600 text-center py-2">
                {notificationChannels.filter((c) => c.channel_type.toLowerCase() === "lark").length}{" "}
                channel(s) configured
              </div>
            )}
          </CardFooter>
        </Card>

        {/* WeCom Channel Card */}
        <Card className="shadow-none border-[#DFDFDF] bg-[#F8F8F8]">
          <CardHeader>
            <CardTitle className="text-[14px]">
              {t("workspace.integrationsPage.notificationChannels.wecom.title")}
            </CardTitle>
            <CardDescription className="text-[12px] min-h-[50px]">
              {t("workspace.integrationsPage.notificationChannels.wecom.desc")}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-2">
            <Button
              variant="outline"
              className="w-full justify-center gap-2 text-[12px] font-normal"
              onClick={() => handleOpenAddChannel("wecom")}
            >
              <InlineSvg content={wecomSvg} size={16} />
              {t("workspace.integrationsPage.notificationChannels.wecom.connect")}
            </Button>
            {notificationChannels.filter((c) => c.channel_type.toLowerCase() === "wecom").length >
              0 && (
              <div className="w-full text-xs text-gray-600 text-center py-2">
                {
                  notificationChannels.filter((c) => c.channel_type.toLowerCase() === "wecom")
                    .length
                }{" "}
                channel(s) configured
              </div>
            )}
          </CardFooter>
        </Card>
      </div>

      {/* Unified Notification Channel Dialog - 仅用于创建 */}
      <NotificationChannelDialog
        open={addChannelOpen && !editingChannel}
        onOpenChange={(open) => {
          setAddChannelOpen(open);
          if (!open) {
            setEditingChannel(null);
            setEditingChannelEvents([]);
          }
        }}
        channelType={selectedChannelType}
        mode="create"
        onSubmit={handleCreateChannel}
        loading={loading}
      />

      {/* Edit Notification Channel Dialog - 专门用于编辑 */}
      <EditNotificationChannelDialog
        open={addChannelOpen && !!editingChannel}
        onOpenChange={(open) => {
          setAddChannelOpen(open);
          if (!open) {
            setEditingChannel(null);
            setEditingChannelEvents([]);
          }
        }}
        channelName={editingChannel?.name || ""}
        channelType={editingChannel?.channel_type as NotificationChannelType}
        webhookUrl={editingChannel?.webhook_url || ""}
        initialEnabledEvents={editingChannelEvents}
        onSubmit={handleUpdateChannel}
        loading={loading}
      />

      {notificationChannels.length > 0 && null}

      <UpgradePlanModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        currentPlan={currentPlan}
        defaultSelectedPlan="professional"
        noticeTitle={upgradeNoticeTitle}
        noticeDescription={upgradeNoticeDescription}
      />
    </div>
  );
}
